import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, LoaderCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const WaitlistForm = ({ dark = false, source = "general" }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      setState("error");
      return;
    }
    setState("loading");
    setError("");
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: source }),
      });
      if (!res.ok) throw new Error();
      setState("success");
    } catch {
      setError("Something went wrong. Please try again.");
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`inline-flex items-center gap-3 rounded-full px-7 py-4 ${
          dark ? "bg-white/10 border border-white/15" : "bg-diem-ok/10 border border-diem-ok/30"
        }`}
        data-testid="waitlist-success"
      >
        <span className="w-8 h-8 rounded-full bg-diem-ok flex items-center justify-center shrink-0">
          <Check className="w-4 h-4 text-white" />
        </span>
        <p className={`font-semibold ${dark ? "text-white" : "text-diem-ink"}`}>
          You're on the list. We'll be in touch.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-xl" data-testid="waitlist-form" noValidate>
      <div
        className={`flex flex-col sm:flex-row gap-3 sm:gap-0 rounded-[2rem] sm:rounded-full p-2 ${
          dark ? "bg-white/10 border border-white/15" : "bg-white border border-diem-line shadow-[0_16px_44px_-12px_rgba(15,23,42,0.18)]"
        }`}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === "error") setState("idle"); }}
          placeholder="you@example.com"
          aria-label="Email address"
          data-testid="waitlist-email-input"
          className={`flex-1 bg-transparent px-5 py-3.5 text-[15px] font-medium outline-none ${
            dark ? "text-white placeholder:text-white/40" : "text-diem-ink placeholder:text-diem-muted"
          }`}
        />
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="waitlist-submit-button"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-diem-blue text-white font-semibold px-7 py-3.5 shadow-[0_10px_30px_rgba(0,85,255,0.45)] hover:shadow-[0_14px_40px_rgba(0,85,255,0.55)] active:scale-[0.98] transition-[box-shadow,transform] duration-300 disabled:opacity-70"
        >
          {state === "loading" ? (
            <LoaderCircle className="w-5 h-5 animate-spin" />
          ) : (
            <>
              Join the waitlist
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </div>
      {state === "error" && (
        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className={`mt-3 text-sm font-semibold ${dark ? "text-red-300" : "text-diem-err"}`}
          data-testid="waitlist-error"
        >
          {error}
        </motion.p>
      )}
    </form>
  );
};
